import { Controller } from "@hotwired/stimulus"

const ENTRY = ".message-card"

// Keeps the message list scrolled to the newest entry: on open (so a thread shows its latest reply)
// and whenever a newer one arrives — the turbo_stream reply, a comment, or a live refresh.
//
// Only when what arrived IS the newest entry, which is the whole subtlety here. The observer watches
// the subtree, so it also sees a note being corrected in place, a reaction landing on something read
// hours ago, and every keystroke into an inline edit form. Following any of those to the bottom
// would throw the reader out of the note they were working in.
export default class extends Controller {
  connect() {
    this.scrollToBottom()
    this.observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => this.bringsNewestEntry(mutation))) this.scrollToBottom()
    })
    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    this.observer?.disconnect()
  }

  bringsNewestEntry(mutation) {
    return Array.from(mutation.addedNodes).some((node) => this.isNewestEntry(node))
  }

  // Last in the whole list rather than "an entry at all": replacing a card mid-thread hands over
  // the same kind of node an append does, and only its position tells the two apart.
  //
  // Last in the LIST and not merely among its siblings, because a page of older entries arrives in
  // a frame of its own — so its bottom card is the last child of that frame while sitting near the
  // top of the thread, and following it would fling the reader to the newest message instead.
  isNewestEntry(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return false
    if (!node.matches(ENTRY)) return false

    return node === Array.from(this.element.querySelectorAll(ENTRY)).at(-1)
  }

  // The other direction: a page of older entries lands ABOVE everything the reader is looking at,
  // so a scroller that kept its scrollTop would shove the card they were reading down the screen by
  // however tall the new page turned out to be. Measured from the BOTTOM instead, which is the one
  // edge the insertion does not move — the browser's own scroll anchoring would do this, but only
  // in the engines that implement it, and "did the thread jump" is not a thing to leave to chance.
  //
  // Restored inside Turbo's render rather than on turbo:frame-render, which comes two repaints
  // later: in between, the new page painted at the top of the scroller and then jumped, and a click
  // aimed at its "Show earlier" link in that window landed on whatever moved in under it.
  //
  // Only for the page frame this is bound on. A collapsed card expanding inside that page renders
  // a frame of its own, and its event bubbles through here — holding the bottom then would push the
  // card the reader just opened up and out of view.
  hold(event) {
    if (event.target !== event.currentTarget) return

    const heldFromBottom = this.element.scrollHeight - this.element.scrollTop
    const render = event.detail.render
    event.detail.render = (currentElement, newElement) => {
      render(currentElement, newElement)
      this.element.scrollTop = this.element.scrollHeight - heldFromBottom
    }
  }

  scrollToBottom() {
    this.element.scrollTop = this.element.scrollHeight
  }
}
